import stateTypes from './stateTypes';
import { logState } from '../logger';

import type { GymDashboardData } from './appInitialState';
import type {
  Exercise,
  Group,
  Gym,
  GymProduct,
  Manager,
  Member,
  Muscle,
  Program,
  Trainer,
  TrainingRoutineTemplate,
} from '../types';

const {
  RESET_ALL_STATE,
  SET_EXERCISES,
  SET_MUSCLES,
  SET_SELECTED_GYM_ID,
  SET_LOGIN_MANAGER_ID,
  SET_LOGIN_TRAINER_ID,
  SET_LOGIN_MEMBER_ID,
  SET_GYM_DASHBOARD_DATA,
  SET_EXERCISE,
  SET_REMOVED_EXERCISE,
  SET_REMOVED_GROUP,
  SET_REMOVED_PROGRAM,
  SET_REMOVED_TEMPLATE,
  SET_GROUP,
  SET_MANAGERS,
  SET_TRAINERS,
  SET_MEMBERS,
  SET_REMOVED_MEMBER,
  SET_REMOVED_TRAINER,
  SET_GYM,
  SET_PROGRAM,
  SET_TEMPLATE,
  SET_REMOVED_MANAGER,
  SET_GYM_PRODUCTS,
} = stateTypes;

export const resetAllState = () => {
  logState('resetAllState');
  return {
    type: RESET_ALL_STATE,
  };
};

export const setExercises = (exercises: Record<string, Exercise>) => {
  logState('setExercises', { count: Object.keys(exercises || {}).length });
  return {
    type: SET_EXERCISES,
    exercises,
  };
};

export const setMuscles = (muscles: Record<string, Muscle>) => {
  logState('setMuscles', { count: Object.keys(muscles || {}).length });
  return {
    type: SET_MUSCLES,
    muscles,
  };
};

export const setSelectedGymId = (selectedGymId: string | null) => {
  logState('setSelectedGymId', { selectedGymId });
  return {
    type: SET_SELECTED_GYM_ID,
    selectedGymId,
  };
};

export const setLoginManagerId = (loginManagerId: string | null) => {
  logState('setLoginManagerId', { loginManagerId });
  return {
    type: SET_LOGIN_MANAGER_ID,
    loginManagerId,
  };
};

export const setLoginTrainerId = (loginTrainerId: string | null) => {
  logState('setLoginTrainerId', { loginTrainerId });
  return {
    type: SET_LOGIN_TRAINER_ID,
    loginTrainerId,
  };
};

export const setLoginMemberId = (loginMemberId: string | null) => {
  logState('setLoginMemberId', { loginMemberId });
  return {
    type: SET_LOGIN_MEMBER_ID,
    loginMemberId,
  };
};

export const setGymDashboardData = (gymDashboardData: GymDashboardData) => {
  logState('setGymDashboardData', { gymId: gymDashboardData.gym.id });
  return {
    type: SET_GYM_DASHBOARD_DATA,
    gymDashboardData,
  };
};

export const setExercise = (exercise: Exercise) => {
  logState('setExercise', { exerciseId: exercise.id });
  return {
    type: SET_EXERCISE,
    exercise,
  };
};

export const setRemovedExercise = (exerciseId: string, trainerId: string) => {
  logState('setRemovedExercise', { exerciseId, trainerId });
  return {
    type: SET_REMOVED_EXERCISE,
    exerciseId,
    trainerId,
  };
};

export const setRemovedGroup = (groupId: string, trainerId: string) => {
  logState('setRemovedGroup', { groupId, trainerId });
  return {
    type: SET_REMOVED_GROUP,
    groupId,
    trainerId,
  };
};

export const setRemovedProgram = (programId: string, trainerId: string) => {
  logState('setRemovedProgram', { programId, trainerId });
  return {
    type: SET_REMOVED_PROGRAM,
    programId,
    trainerId,
  };
};

export const setRemovedTemplate = (templateId: string, trainerId: string) => {
  logState('setRemovedTemplate', { templateId, trainerId });
  return {
    type: SET_REMOVED_TEMPLATE,
    templateId,
    trainerId,
  };
};

export const setGroup = (group: Group) => {
  logState('setGroup', { groupId: group.id });
  return {
    type: SET_GROUP,
    group,
  };
};

export const setManagers = (managers: Manager[]) => {
  logState('setManagers', { count: managers?.length || 0 });
  return {
    type: SET_MANAGERS,
    managers,
  };
};

export const setTrainers = (trainers: Trainer[]) => {
  logState('setTrainers', { count: trainers?.length || 0 });
  return {
    type: SET_TRAINERS,
    trainers,
  };
};

export const setMembers = (members: Member[]) => {
  logState('setMembers', { count: members?.length || 0 });
  return {
    type: SET_MEMBERS,
    members,
  };
};

export const setRemovedMember = (memberId: string, trainerIds: string[]) => {
  logState('setRemovedMember', {
    memberId,
    trainerIdsCount: trainerIds?.length || 0,
  });
  return {
    type: SET_REMOVED_MEMBER,
    memberId,
    trainerIds,
  };
};

export const setRemovedTrainer = (trainerId: string) => {
  logState('setRemovedTrainer', { trainerId });
  return {
    type: SET_REMOVED_TRAINER,
    trainerId,
  };
};

export const setGym = (gym: Gym) => {
  logState('setGym', { gymId: gym.id });
  return {
    type: SET_GYM,
    gym,
  };
};

export const setProgram = (program: Program) => {
  logState('setProgram', { programId: program.id });
  return {
    type: SET_PROGRAM,
    program,
  };
};

export const setTemplate = (template: TrainingRoutineTemplate) => {
  logState('setTemplate', { templateId: template.id });
  return {
    type: SET_TEMPLATE,
    template,
  };
};

export const setRemovedManager = (managerId: string) => {
  logState('setRemovedManager', { managerId });
  return {
    type: SET_REMOVED_MANAGER,
    managerId,
  };
};

export const setGymProducts = (gymId: string, gymProducts: GymProduct[]) => {
  logState('setGymProducts', { gymId, count: gymProducts?.length || 0 });
  return {
    type: SET_GYM_PRODUCTS,
    gymId,
    gymProducts,
  };
};
